import CONFIG from '@/config/config.ts';
import {
  SingleQuestionDefinitionDto,
  UserQuizQuestionAnswerDto,
  type UserQuizStateDto,
} from '@/api/generated';
import { DomPurifyConfig } from '@/config/DomPurify.config.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import SingleQuestionForm from '@/views/process/components/quizInialized/form/SingleQuestionForm.tsx';
import QuestionWrapper from '@/views/process/components/quizInialized/questions/QuestionWrapper.tsx';
import DOMPurify from 'dompurify';
import parseHtml from 'html-react-parser';
import { FC, Fragment, useEffect, useRef, useState } from 'react';

const audioUrl = 'https://lets-orange.b-cdn.net/lektor-orange-2026/ORANGE_5.mp3';
const videoUrl = `${CONFIG.CDN_VIDEO_URL}/robot-inhouse-02.mp4`;

export type QuestionSegment4Props = {
  disabled: boolean;
  onAnswerSelected: (
    questionDefinition: UserQuizQuestionAnswerDto,
  ) => Promise<UserQuizStateDto | undefined>;
  questionDefinition: {
    discriminatorType: 'SINGLE';
  } & SingleQuestionDefinitionDto;
};

const QuestionSegment4: FC<QuestionSegment4Props> = ({
  disabled,
  onAnswerSelected,
  questionDefinition,
}) => {
  const { t } = useTranslationWithPrefix('views.process.quizQuestions');
  const [questionDefinitions, setQuestionDefinitions] = useState<
    QuestionSegment4Props['questionDefinition'][]
  >([]);
  const selfRef = useRef<HTMLDivElement>(null);

  const endContentVisible = questionDefinition?.question === 'LEKTOR_4_1';

  useEffect(() => {
    if (!endContentVisible) {
      return;
    }
    let timeout: ReturnType<typeof setTimeout>;
    if (selfRef.current) {
      timeout = setTimeout(() => {
        selfRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 200);
    }
    return () => clearTimeout(timeout);
  }, [endContentVisible]);

  const handleAnswer = async (value: UserQuizQuestionAnswerDto) => {
    const response = await onAnswerSelected(value);
    if (!endContentVisible) {
      setQuestionDefinitions(state => [...state, questionDefinition]);
    }
    return response;
  };

  const handleRefresh = () => {
    void handleAnswer({
      answerIndex: 0,
      discriminatorType: questionDefinition.discriminatorType,
    });
  };
  const content =
    !questionDefinitions.length && endContentVisible ? null : (
      <>
        {questionDefinitions.map((item, index) => (
          <Fragment key={item?.question}>
            <SingleQuestionForm
              disabled={disabled}
              isCurrent={false}
              onAnswerSelected={handleAnswer}
              onlyTwoOptions
              questionDefinition={item}
              readonly={index < questionDefinitions.length}
              withoutSubmitButton
            />
            {index !== questionDefinitions.length - 1 && (
              <div className="border-top border-light my-3"></div>
            )}
          </Fragment>
        ))}
      </>
    );

  return (
    <QuestionWrapper
      disabledButton={disabled}
      {...(endContentVisible && {
        hasNext: true,
        onNext: handleRefresh,
      })}
      audioUrl={audioUrl}
      formComponent={
        <>
          {content}
          {endContentVisible ? (
            <div ref={selfRef}>
              <p className="lead">{t('questions.4.content.5')}</p>
              <p className="lead">{t('questions.4.content.6')}</p>
            </div>
          ) : (
            <>
              <SingleQuestionForm
                disabled={disabled}
                isCurrent
                onAnswerSelected={handleAnswer}
                onlyTwoOptions
                questionDefinition={questionDefinition}
                withoutSubmitButton
              />
            </>
          )}
        </>
      }
      segmentContent={
        <>
          <p className="lead fw-bold">{t('questions.4.titles.1')}</p>
          <div className="flex flex-column max-w-(--breakpoint-md) gap-s">
            <p className="lead">{t('questions.4.content.1')}</p>
            <p className="lead">
              {parseHtml(DOMPurify.sanitize(t('questions.4.content.2'), DomPurifyConfig))}
            </p>
          </div>
        </>
      }
      videoUrl={videoUrl}
    />
  );
};

export default QuestionSegment4;
